import * as React from 'react';
import * as L from "leaflet";
import 'leaflet/dist/leaflet.css';

import "./radius-map-control.scss";

import {cs}        from "@Common/react/chain-services";
import {fragments} from "@Common/react/fragments";
import {keyed}     from "@Common/react/keyed";
import {UseState}  from "@Common/react/use-state";
import {Watch}     from '@Common/react/watch';
import {equalDeep} from '@Common/utils/objects';
import {Static2}   from '@Common/react/static-2';

import {LeafletMap} from "../../../../common/leaflet-map/leaflet-map";
import {cx}         from 'emotion';

export const RadiusMapControl = ({
    center,
    hoveredMarker,
    markers=undefined,
    viewSize,
    totalAddresses,
    selectedAddressCount
}) => cs(
    ["cursorPosition", (_, next) => UseState({
        next,
    })],
    ["circleRef", (_, next) => Static2({next})],
    ["pillRef", (_, next) => Static2({next})],
    ["leafletMap", ({cursorPosition, circleRef, pillRef}, next) => LeafletMap({
        center,
        onMouseMove: (e) => cursorPosition.onChange({pos: getELoc(e.originalEvent), latlng: e.latlng}),
        onZoomEnd: ({map, prevZoom, renderedElements}) => {
            hoveredMarker.onChange(null);
            onZoomEnd({map, prevZoom, renderedElements, circleRef, pillRef, totalAddresses, selectedAddressCount});
        },
        renderItems: ({map, zoom, renderedElements}) => renderItems({map, zoom, renderedElements, circleRef, pillRef, totalAddresses, selectedAddressCount}),
        isSidebarClosed: false,
        xdQaPrefix: "Build Radius List",
        next,
    })],
    ({leafletMap, cursorPosition, circleRef, pillRef}) => fragments(
        (
            <div className="radius-map-9p7">
                <div className={cx("map", viewSize)} ref={leafletMap.ref}/>

                {hoveredMarker.value && cursorPosition.value && (
                    InfoBox({
                        hoveredMarker: hoveredMarker.value,
                        position: cursorPosition.value,
                    })
                )}
            </div>
        ),
        markers?.map((marker) => cs(
            keyed(marker.elementKey),
            () => (
                leafletMap.createMarker({
                    marker,
                    onMouseOver: marker.onMouseOver,
                    onMouseOut : marker.onMouseOut,
                    markerType : marker.isGrouped ? "building" : "point"
                })
            )
        )),
        Watch({
            value: markers,
            compare: (s1, s2) => equalDeep(s1?.map(s => s.elementKey), s2?.map(s => s.elementKey)),
            onChanged: () => {
                leafletMap.cleanMap({
                    elements: markers
                });
                leafletMap.cleanItems([circleRef, pillRef]);
            },
        }),
        Watch({
            value: viewSize,
            onChanged: () => leafletMap.invalidateSize(),
        }),
        Watch({
            value: selectedAddressCount,
            onChanged: () => {
                const newPillIcon = L.divIcon({
                    className: 'pill-icon',
                    html: `<div class="pill">${selectedAddressCount}</div>`,
                    iconSize: [80, 30],
                    iconAnchor: [40, 15],
                })
                pillRef.get()?.setIcon(newPillIcon);
            }
        })
    )
);

const getELoc = (e) => {
    let x = e.clientX;
    let y = e.clientY;

    return {x, y};
};

const InfoBox = ({hoveredMarker, position}) => {
    return (
        <div className="info-box" style={{ top: position.pos.y - 60, left: position.pos.x - 150 }}>
            <div className="box address">
                <div
                    className="value"
                    xd-qa-type="label"
                    xd-qa-name="Build Radius List Hovered Address"
                >
                    {hoveredMarker.name}
                </div>
                <div
                    className="label"
                    xd-qa-type="label"
                    xd-qa-name="Build Radius List Hovered Label"
                >
                    {hoveredMarker.label}
                </div>
            </div>
            <div className="box zip">
                <div
                    className="value"
                    xd-qa-type="label"
                    xd-qa-name="Build Radius List Hovered Zip Code"
                >
                    {hoveredMarker.zip}
                </div>
                <div
                    className="label"
                    xd-qa-type="label"
                    xd-qa-name="Build Radius List Hovered Zip Label"
                >
                    Zip Code
                </div>
            </div>
        </div>
    )
};

const onZoomEnd = ({map, prevZoom, renderedElements, circleRef, pillRef, totalAddresses, selectedAddressCount}) => {
    const zoom = map.getZoom();
    if (zoom < 16 || (zoom >= 16 && prevZoom.value < 16)) {
        renderItems({map, zoom, renderedElements, circleRef, pillRef, totalAddresses, selectedAddressCount});
    }
    prevZoom.onChange(zoom);
};

const renderItems = ({map, zoom, renderedElements, circleRef, pillRef, totalAddresses, selectedAddressCount}) => {
    if (circleRef.get()) {
        map.removeLayer(circleRef.get());
    }
    if (pillRef.get()) {
        map.removeLayer(pillRef.get());
    }

    if (zoom >= 16) {
        renderedElements?.forEach(({ref}) => !map.hasLayer(ref.get()) && ref.get().addTo(map));
    } else {
        const [latTot, lngTot] = renderedElements?.reduce(([latTot, lngTot], {ref}) => {
            const {lat, lng} = ref.get().getLatLng();
            return [latTot + lat, lngTot + lng];
        }, [0, 0]);

        const centerLat = latTot / renderedElements.length;
        const centerLng = lngTot / renderedElements.length;
        const radius = Math.max(...renderedElements.map(({ref}) => ref.get().getLatLng().distanceTo([centerLat, centerLng])));

        const radiusInPixels = radius / (156543.03392 * Math.cos(centerLat * Math.PI / 180) / Math.pow(2, zoom));
        const density = totalAddresses / (Math.PI * radiusInPixels * radiusInPixels);

        if (density > 0.001) {
            let clickZoom = zoom;
            let clickDensity = density;
            while (clickZoom < 16 && clickDensity > 0.001) {
                clickZoom++;
                let clickRadius = radius / (156543.03392 * Math.cos(centerLat * Math.PI / 180) / Math.pow(2, clickZoom));
                clickDensity = totalAddresses / (Math.PI * clickRadius * clickRadius);
            }

            renderedElements.forEach(({ref}) => map.removeLayer(ref.get()));
            const circle = L.circle([centerLat, centerLng], {
                radius,
                color: '#249AF3',
                fillOpacity: 0.1,
            });
            circle.on('click', (e) => {
                map.setZoomAround(e.latlng, clickZoom);
            })
            circle.on('add', () => {
                const circleElement = circle.getElement();
                circleElement.setAttribute("xd-qa-name", "Map Circle Marker");
                circleElement.setAttribute("xd-qa-type", "icon");
            })

            circleRef.set(circle);
            circle.addTo(map);

            if (2*radiusInPixels > 100) {
                const pillIcon = L.divIcon({
                    className: 'pill-icon',
                    html: `<div class="pill" xd-qa-type="label" xd-qa-name="Build Radius List Map Address Count">${selectedAddressCount}</div>`,
                    iconSize: [80, 30],
                    iconAnchor: [40, 15],
                })
                const pill = L.marker([centerLat, centerLng], {icon: pillIcon});
                pill.on('click', (e) => {
                    map.setZoomAround(e.latlng, clickZoom);
                });
                pillRef.set(pill);
                pill.addTo(map);
            }
        } else {
            renderedElements?.forEach(({ref}) => !map.hasLayer(ref.get()) && ref.get().addTo(map));
        }
    }
}